import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import classnames from "classnames";
import bluedotService from "../../services/bluedot";
import bluedotConfig from "../../config/bluedot";
import {
  isNativeAppWebView,
  getBluedotEnvironmentInfo
} from "../../utils/bluedotWebView";
import {
  initializeBluedotWithPermissionCheck,
  getBluedotIntegrationStatus
} from "../../utils/bluedotWebViewIntegration";
import {
  initializeBluedot,
  checkNativePermission,
  requestNativePermission
} from "../../store/bluedot/actions";
import * as styles from "./index.module.scss";

const StatusIndicator = ({ label, value, active }) => (
  <div className={classnames(styles.StatusItem, { [styles.Active]: active })}>
    <span className={styles.StatusDot} />
    <span className={styles.StatusLabel}>{label}:</span>
    <span className={styles.StatusValue}>{value ? "Yes" : "No"}</span>
  </div>
);

const BluedotDebugHeader = ({
  nativePermissionGranted,
  nativePermissionChecked,
  nativePermissionStatus,
  dispatch
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [environment, setEnvironment] = useState("checking");
  const [sdkStatus, setSdkStatus] = useState(null);
  const [initError, setInitError] = useState(null);
  const [waveResult, setWaveResult] = useState(null); // { success, message, timestamp }
  const [isSendingWave, setIsSendingWave] = useState(false);
  const [testDestinationId, setTestDestinationId] = useState("");
  // Geo-Triggering state (WebView / native only)
  const [geoTriggeringStatus, setGeoTriggeringStatus] = useState("stopped"); // "stopped" | "starting" | "running" | "error"
  const [geoTriggeringMessage, setGeoTriggeringMessage] = useState(null);

  const isConfigEnabled = bluedotConfig.enabled || bluedotConfig.debugMode;

  useEffect(() => {
    const isWebView = isNativeAppWebView();
    setEnvironment(isWebView ? "WebView" : "Standalone");

    // Listen for manual environment override
    const handleMessage = (event) => {
      try {
        const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        if (data && data.type === "BLUEDOT_SET_MOBILE_ENV") {
          console.log("[Bluedot Debug] Force switching to WebView environment");
          setEnvironment("WebView");
        }
      } catch (e) {
        // Ignore parsing errors
      }
    };

    if (typeof window !== "undefined") {
      window.addEventListener("message", handleMessage);
    }

    // Initialize Bluedot via Redux (updates Redux state)
    dispatch(initializeBluedot());

    // Also initialize via service (ensures SDK is ready)
    initializeBluedotWithPermissionCheck()
      .then(result => {
        setSdkStatus(getBluedotIntegrationStatus());
        if (!result.success) {
          setInitError(result.error);
        }
      })
      .catch(error => {
        setInitError(error.message);
        setSdkStatus(getBluedotIntegrationStatus());
      });

    // Listen for native Geo-Triggering responses (WebView only)
    if (isWebView && typeof window !== "undefined") {
      window.onNativeGeoTriggeringResponse = response => {
        try {
          const parsed =
            typeof response === "string" ? JSON.parse(response) : response;
          if (parsed.success) {
            setGeoTriggeringStatus(parsed.action === "stop" ? "stopped" : "running");
            setGeoTriggeringMessage({
              success: true,
              message: parsed.action === "stop"
                ? "Geo-Triggering stopped"
                : "Geo-Triggering started - listening for zone events",
              timestamp: new Date().toLocaleTimeString()
            });
          } else {
            setGeoTriggeringStatus("error");
            setGeoTriggeringMessage({
              success: false,
              message: parsed.error || "Geo-Triggering failed",
              timestamp: new Date().toLocaleTimeString()
            });
          }
        } catch (e) {
          setGeoTriggeringStatus("error");
          setGeoTriggeringMessage({
            success: false,
            message: "Failed to parse native response",
            timestamp: new Date().toLocaleTimeString()
          });
        }
      };
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("message", handleMessage);
        delete window.onNativeGeoTriggeringResponse;
      }
    };
  }, []);

  // Refresh status periodically
  useEffect(() => {
    if (!sdkStatus) return;

    const interval = setInterval(() => {
      setSdkStatus(getBluedotIntegrationStatus());
    }, 2000);

    return () => clearInterval(interval);
  }, [sdkStatus]);

  // Send test wave via Web SDK — standalone browser ONLY.
  // NEVER call from WebView (use Geo-Triggering instead per integration doc).
  const handleSendTestWave = async () => {
    if (environment === "WebView") {
      console.warn("[Bluedot Debug] Wave sending is not available in WebView — use Geo-Triggering instead");
      return;
    }

    const destinationId = testDestinationId.trim();
    if (!destinationId) {
      setWaveResult({
        success: false,
        message: "Enter a Destination ID (Zone ID from Canvas)",
        timestamp: new Date().toLocaleTimeString()
      });
      return;
    }

    setIsSendingWave(true);
    setWaveResult(null);

    const waveData = {
      eventType: "onTheWay",
      orderId: `debug-${Date.now()}`,
      hs_Customer_Name: "Debug Test",
      source: "web-standalone"
    };

    try {
      console.log("[Bluedot Debug] Sending wave to:", destinationId, waveData);
      const result = await bluedotService.sendWave(destinationId, waveData);

      const success = result && !result.error;
      console.log("[Bluedot Debug] Wave result:", result);

      // Extract error message from result
      let errorMsg = "Unknown";
      if (result && result.error) {
        errorMsg =
          typeof result.error === "string"
            ? result.error
            : result.error.message || JSON.stringify(result.error);
      }

      setWaveResult({
        success,
        message: success
          ? `Wave sent to "${destinationId}"`
          : `${errorMsg}`,
        timestamp: new Date().toLocaleTimeString()
      });
    } catch (error) {
      console.error("[Bluedot Debug] Wave send error:", error);
      const msg =
        error && error.message
          ? error.message
          : typeof error === "string"
          ? error
          : JSON.stringify(error);
      setWaveResult({
        success: false,
        message: msg,
        timestamp: new Date().toLocaleTimeString()
      });
    } finally {
      setIsSendingWave(false);
      setSdkStatus(getBluedotIntegrationStatus());
    }
  };

  const handleStartGeoTriggering = () => {
    if (typeof window === "undefined") return;

    setGeoTriggeringStatus("starting");
    setGeoTriggeringMessage(null);

    const message = JSON.stringify({
      type: "BLUEDOT_START_GEO_TRIGGERING",
      payload: { timestamp: new Date().toISOString() }
    });

    try {
      if (window.ReactNativeWebView && window.ReactNativeWebView.postMessage) {
        window.ReactNativeWebView.postMessage(message);
      } else if (window.postMessage) {
        window.postMessage(message, "*");
      }
    } catch (err) {
      console.error("[Bluedot Debug] Failed to send start tracking message:", err);
      setGeoTriggeringStatus("error");
      setGeoTriggeringMessage({
        success: false,
        message: "Failed to communicate with native app",
        timestamp: new Date().toLocaleTimeString()
      });
    }
  };

  const handleStopGeoTriggering = () => {
    if (typeof window === "undefined") return;

    const message = JSON.stringify({
      type: "BLUEDOT_STOP_GEO_TRIGGERING",
      payload: { timestamp: new Date().toISOString() }
    });

    try {
      if (window.ReactNativeWebView && window.ReactNativeWebView.postMessage) {
        window.ReactNativeWebView.postMessage(message);
      } else if (window.postMessage) {
        window.postMessage(message, "*");
      }
    } catch (err) {
      console.error("[Bluedot Debug] Failed to send stop tracking message:", err);
    }
  };

  const handleReset = () => {
    bluedotService.reset();
    setSdkStatus(getBluedotIntegrationStatus());
  };

  const handleCheckNativePermission = () => {
    dispatch(checkNativePermission());
  };

  const handleRequestNativePermission = () => {
    dispatch(requestNativePermission());
  };

  // Use service status directly, fallback to Redux
  const status = sdkStatus || {};
  const {
    enabled = bluedotConfig.enabled,
    initialized = false,
    locationEnabled = false,
    waveSending = false,
    error = initError,
    initializing = false,
    locationRequesting = false,
    nativePermissionGranted: serviceNativeGranted = false,
    nativePermissionChecked: serviceNativeChecked = false,
    nativePermissionStatus: serviceNativeStatus = "unknown"
  } = status;

  // Use service values for native permission if available
  const actualNativeGranted = sdkStatus
    ? serviceNativeGranted
    : nativePermissionGranted;
  const actualNativeChecked = sdkStatus
    ? serviceNativeChecked
    : nativePermissionChecked;
  const actualNativeStatus = sdkStatus
    ? serviceNativeStatus
    : nativePermissionStatus;

  // Default to "unknown" if not checked yet
  const displayPermissionStatus = actualNativeStatus || "unknown";

  // if (!isConfigEnabled) {
  //   return null;
  // }

  return (
    <div
      className={classnames(styles.DebugHeader, {
        [styles.Expanded]: isExpanded
      })}
    >
      <div
        className={styles.ToggleBar}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span className={styles.Title}>🔵 Bluedot Debug {environment}</span>
        <span
          className={classnames(styles.Chevron, {
            [styles.ChevronUp]: isExpanded
          })}
        >
          ▼
        </span>
      </div>

      {isExpanded && (
        <div className={styles.Content}>
          <div className={styles.Section}>
            <h4 className={styles.SectionTitle}>Status</h4>
            <div className={styles.StatusGrid}>
              <StatusIndicator
                label="Config Enabled"
                value={enabled}
                active={enabled}
              />
              <StatusIndicator
                label="SDK Initialized"
                value={initialized}
                active={initialized}
              />
              <StatusIndicator
                label="Location Enabled"
                value={locationEnabled}
                active={locationEnabled}
              />
              <StatusIndicator
                label="Wave Sending"
                value={waveSending}
                active={waveSending}
              />
            </div>
            {error && <div className={styles.Error}>Error: {error}</div>}
            {!initialized && !error && (
              <div className={styles.Error}>
                ⚠️ SDK initializing or not started
              </div>
            )}
          </div>

          <div className={styles.Section}>
            <h4 className={styles.SectionTitle}>SDK Status</h4>
            <div className={styles.DetailsGrid}>
              <div className={styles.DetailItem}>
                <span className={styles.DetailLabel}>Initializing:</span>
                <span className={styles.DetailValue}>
                  {initializing ? "Yes" : "No"}
                </span>
              </div>
              <div className={styles.DetailItem}>
                <span className={styles.DetailLabel}>Location Requested:</span>
                <span className={styles.DetailValue}>
                  {locationRequesting ? "Yes" : "No"}
                </span>
              </div>
              <div className={styles.DetailItem}>
                <span className={styles.DetailLabel}>Debug Mode:</span>
                <span className={styles.DetailValue}>
                  {bluedotConfig.debugMode ? "Yes" : "No"}
                </span>
              </div>
              <div className={styles.DetailItem}>
                <span className={styles.DetailLabel}>Mock Mode:</span>
                <span className={styles.DetailValue}>
                  {bluedotConfig.mockMode ? "Yes" : "No"}
                </span>
              </div>
            </div>
          </div>

          {environment === "WebView" && (
            <div className={styles.Section}>
              <h4 className={styles.SectionTitle}>Native Permission</h4>
              <div className={styles.DetailsGrid}>
                <div className={styles.DetailItem}>
                  <span className={styles.DetailLabel}>Permission Status:</span>
                  <span className={styles.DetailValue}>
                    {displayPermissionStatus}
                  </span>
                </div>
                <div className={styles.DetailItem}>
                  <span className={styles.DetailLabel}>
                    Permission Granted:
                  </span>
                  <span className={styles.DetailValue}>
                    {actualNativeGranted ? "Yes" : "No"}
                  </span>
                </div>
                <div className={styles.DetailItem}>
                  <span className={styles.DetailLabel}>Checked:</span>
                  <span className={styles.DetailValue}>
                    {actualNativeChecked ? "Yes" : "No"}
                  </span>
                </div>
              </div>
              <div className={styles.Actions}>
                <button
                  className={styles.ActionButton}
                  onClick={handleCheckNativePermission}
                >
                  Check Permission
                </button>
                <button
                  className={styles.ActionButton}
                  onClick={handleRequestNativePermission}
                >
                  Request Permission
                </button>
              </div>
            </div>
          )}

          {environment === "WebView" && (
            <div className={styles.Section}>
              <h4 className={styles.SectionTitle}>Geo-Triggering (Native)</h4>
              <div className={styles.DetailsGrid}>
                <div className={styles.DetailItem}>
                  <span className={styles.DetailLabel}>Status:</span>
                  <span className={styles.DetailValue}>
                    {geoTriggeringStatus}
                  </span>
                </div>
              </div>
              <div className={styles.Actions}>
                <button
                  className={classnames(styles.ActionButton, styles.WaveButton)}
                  onClick={handleStartGeoTriggering}
                  disabled={geoTriggeringStatus === "starting" || geoTriggeringStatus === "running"}
                >
                  {geoTriggeringStatus === "starting"
                    ? "Starting..."
                    : geoTriggeringStatus === "running"
                    ? "Tracking Active"
                    : "Start Tracking"}
                </button>
                <button
                  className={styles.ActionButton}
                  onClick={handleStopGeoTriggering}
                  disabled={geoTriggeringStatus !== "running"}
                >
                  Stop Tracking
                </button>
              </div>
              {geoTriggeringMessage && (
                <div
                  className={classnames(styles.WaveResult, {
                    [styles.WaveSuccess]: geoTriggeringMessage.success,
                    [styles.WaveFailure]: !geoTriggeringMessage.success
                  })}
                >
                  <span>
                    {geoTriggeringMessage.success ? "✓" : "✗"}{" "}
                    {geoTriggeringMessage.message}
                  </span>
                  <span className={styles.WaveTime}>
                    {geoTriggeringMessage.timestamp}
                  </span>
                </div>
              )}
            </div>
          )}

          {environment !== "WebView" && (
            <div className={styles.Section}>
              <h4 className={styles.SectionTitle}>Send Test Wave</h4>
              <div className={styles.InputRow}>
                <label className={styles.InputLabel}>Destination ID:</label>
                <input
                  className={styles.Input}
                  type="text"
                  value={testDestinationId}
                  onChange={e => setTestDestinationId(e.target.value)}
                  placeholder="Zone / Store ID from Canvas"
                />
              </div>
              <div className={styles.Actions}>
                <button
                  className={classnames(styles.ActionButton, styles.WaveButton)}
                  onClick={handleSendTestWave}
                  disabled={isSendingWave || !initialized}
                >
                  {isSendingWave ? "Sending..." : "Send Test Wave"}
                </button>
                <button className={styles.ActionButton} onClick={handleReset}>
                  Reset SDK
                </button>
              </div>
              {waveResult && (
                <div
                  className={classnames(styles.WaveResult, {
                    [styles.WaveSuccess]: waveResult.success,
                    [styles.WaveFailure]: !waveResult.success
                  })}
                >
                  <span>
                    {waveResult.success ? "✓" : "✗"} {waveResult.message}
                  </span>
                  <span className={styles.WaveTime}>
                    {waveResult.timestamp}
                  </span>
                </div>
              )}
            </div>
          )}

          <div className={styles.Section}>
            <h4 className={styles.SectionTitle}>Environment Info</h4>
            <div className={styles.EnvironmentInfo}>
              <pre className={styles.Pre}>
                {JSON.stringify(getBluedotEnvironmentInfo(), null, 2)}
              </pre>
            </div>
          </div>

          <div className={styles.Section}>
            <h4 className={styles.SectionTitle}>Service Status</h4>
            <div className={styles.EnvironmentInfo}>
              <pre className={styles.Pre}>
                {JSON.stringify(
                  sdkStatus || getBluedotIntegrationStatus(),
                  null,
                  2
                )}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  lastWave: state.bluedot?.lastWave || null
});

export default connect(mapStateToProps)(BluedotDebugHeader);
